import { useField } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import normalizeOptions from "./normalizeOptions";
import renderChildren from "../renderChildren";

function normalizeSx(sx: any) {
    if (!sx || typeof sx !== "object" || Array.isArray(sx)) return sx;
    const next = { ...sx };

    if (next.bgColor !== undefined && next.bgcolor === undefined) {
        next.bgcolor = next.bgColor;
        delete next.bgColor;
    }

    return next;
}

function isZeroGap(value: any) {
    return value === 0 || value === "0" || value === "0px";
}

function hasZeroGap(value: any) {
    if (isZeroGap(value)) return true;
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    return Object.values(value).some(isZeroGap);
}

function getResponsiveItemJoinSx(gap: any, radius: any) {
    if (!gap || typeof gap !== "object" || Array.isArray(gap)) return {};

    const next: Record<string, any> = {};

    Object.entries(gap).forEach(([breakpoint, value]) => {
        next[breakpoint] = isZeroGap(value)
            ? {
                ml: "-1px",
                mt: "-1px",
                borderRadius: 0,
            }
            : {
                ml: 0,
                mt: 0,
                borderRadius: radius,
                borderTopLeftRadius: radius,
                borderTopRightRadius: radius,
                borderBottomLeftRadius: radius,
                borderBottomRightRadius: radius,
            };
    });

    return next;
}

function mergeSx(base: any, override: any) {
    return [
        normalizeSx(base),
        override,
    ].filter(Boolean);
}

function injectItemBoxSx(children: any[] = [], sx: any) {
    let injected = false;

    return children.map((child) => {
        if (injected || child?.type !== "box") {
            return child;
        }

        injected = true;

        return {
            ...child,
            props: {
                ...(child.props || {}),
                sx: mergeSx(sx, child.props?.sx),
            },
        };
    });
}

export type InsurerOption = {
    value: string;
    label?: string;
    logo?: string;       // tên hiển thị, vd: "⭐ PVI"
    sub?: string;       // dòng phụ, vd: "INSURANCE"
    logoColor?: string;  // màu chữ logo, vd: "#f97316"
    price?: any;      // vd: "7,000,000 đ"
    [key: string]: any;
};

export type InsurerRadioGroupNodeType = {
    type: "box_group" | "box_radio_group" | "box_checkbox_group" | "radio" | "checkbox";
    name: string;
    inputType?: "radio" | "checkbox";
    options: InsurerOption[];
    columns?: number | Record<string, number>;
    gap?: number | string;
    joined?: boolean;
    radius?: number | string;
    borderColor?: string;
    selectedBorderColor?: string;
    selectedBgcolor?: string;
    bgcolor?: string;
    labelKey?: string;
    titleKey?: string;
    descriptionKey?: string;
    priceKey?: string;
    colorKey?: string;
    sx?: any;
    itemSx?: any;
    selectedItemSx?: any;
    labelSx?: any;
    descriptionSx?: any;
    priceSx?: any;
    onChange?: (value: any, meta: any) => void;
};

const BoxRadioGroupNode = ({
                                   type: propType,
                                   name,
                                   options = [],
                                   inputType,
                                   columns = 3,
                                   gap = "10px",
                                   joined,
                                   radius = "10px",
                                   borderColor = "#e0e0e0",
                                   selectedBorderColor = "#f97316",
                                   selectedBgcolor = "#fff7f0",
                                   bgcolor = "#fafafa",
                                   labelKey = "label",
                                   titleKey = "logo",
                                   descriptionKey = "sub",
                                   priceKey = "price",
                                   colorKey = "logoColor",
                                   sx,
                                   itemSx,
                                   selectedItemSx,
                                   labelSx,
                                   descriptionSx,
                                   priceSx,
                                   onChange,
                                   node,
                                   context,
                               }: InsurerRadioGroupNodeType & any) => {
    const [field, , helpers] = useField(name);
    const items = normalizeOptions(options);
    const selectionType =
        inputType ||
        propType ||
        (node?.type === "box_checkbox_group" ? "checkbox" : "radio");
    const isCheckbox = selectionType === "checkbox";
    const selectedValues = Array.isArray(field.value) ? field.value : [];
    const shouldJoin = joined ?? hasZeroGap(gap);
    const gridColumns =
        typeof columns === "object"
            ? Object.fromEntries(
                Object.entries(columns).map(([key, value]) => [
                    key,
                    `repeat(${value}, minmax(0, 1fr))`,
                ])
            )
            : `repeat(${columns}, minmax(0, 1fr))`;

    const isSelected = (value: any) =>
        isCheckbox ? selectedValues.includes(value) : field.value === value;

    const setValue = (value: any) => {
        if (!isCheckbox) {
            helpers.setValue(value);
            onChange?.(value, {
                field,
                helpers,
                option: items.find((item) => item.value === value),
            });
            return;
        }

        let nextValue;
        if (selectedValues.includes(value)) {
            nextValue = selectedValues.filter((item) => item !== value);
        } else {
            nextValue = [...selectedValues, value];
        }

        helpers.setValue(nextValue);
        onChange?.(nextValue, {
            field,
            helpers,
            option: items.find((item) => item.value === value),
        });
    };

    const getJoinedSx = (index: number) => {
        if (!shouldJoin || typeof columns !== "number") return {};

        const row = Math.floor(index / columns);
        const col = index % columns;
        const lastRow = Math.floor((items.length - 1) / columns);
        const isTop = row === 0;
        const isBottom = row === lastRow;
        const isLeft = col === 0;
        const isRight = col === columns - 1 || index === items.length - 1;

        return {
            ml: isLeft ? 0 : "-1px",
            mt: isTop ? 0 : "-1px",
            borderRadius: 0,
            borderTopLeftRadius: isTop && isLeft ? radius : 0,
            borderTopRightRadius: isTop && isRight ? radius : 0,
            borderBottomLeftRadius: isBottom && isLeft ? radius : 0,
            borderBottomRightRadius: isBottom && isRight ? radius : 0,
            ...getResponsiveItemJoinSx(gap, radius),
        };
    };

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap,
                mb: "20px",
                ...(shouldJoin && typeof columns !== "number"
                    ? {
                        "& > .box-choice-item": {
                            ml: "-1px",
                            mt: "-1px",
                            borderRadius: 0,
                            ...getResponsiveItemJoinSx(gap, radius),
                        },
                        "& > .box-choice-item:first-of-type": {
                            ml: 0,
                            mt: 0,
                            borderTopLeftRadius: radius,
                        },
                        "& > .box-choice-item:last-of-type": {
                            borderBottomRightRadius: radius,
                        },
                    }
                    : {}),
                ...normalizeSx(sx),
            }}
        >
            {items.map((opt, index) => {
                const selected = isSelected(opt.value);
                const item = {
                    ...opt,
                    lable: opt.lable ?? opt.label,
                };
                const title = opt[titleKey] ?? opt[labelKey] ?? opt.value;
                const description = opt[descriptionKey];
                const price = opt[priceKey];
                const color = opt[colorKey] ?? "#f97316";
                const hasCustomTemplate = Boolean(node?.children?.length);
                const itemContext = {
                    ...(context || {}),
                    item,
                    option: item,
                    index,
                    isSelected: selected,
                    type: selectionType,
                    setValue,
                };
                const customItemSx = {
                    position: "relative",
                    zIndex: selected ? 1 : 0,
                    cursor: "pointer",
                };
                const itemBoxSx = {
                    position: "relative",
                    zIndex: selected ? 1 : 0,
                    border: `1px solid ${selected ? selectedBorderColor : borderColor}`,
                    borderRadius: shouldJoin ? 0 : radius,
                    bgcolor: selected ? selectedBgcolor : bgcolor,
                    p: "10px 8px 8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    transition: "border 0.15s, background 0.15s",
                    ...getJoinedSx(index),
                    ...normalizeSx(itemSx),
                    ...(selected ? normalizeSx(selectedItemSx) : {}),
                };
                const defaultItemSx = itemBoxSx;
                const customChildren = hasCustomTemplate
                    ? injectItemBoxSx(node.children, itemBoxSx)
                    : [];
                const customContext = {
                    ...itemContext,
                    itemBoxSx,
                };

                return (
                    <Box
                        className="box-choice-item"
                        key={opt.value}
                        onClick={() => setValue(opt.value)}
                        sx={hasCustomTemplate ? customItemSx : defaultItemSx}
                    >
                        {hasCustomTemplate ? (
                            renderChildren(customChildren, customContext)
                        ) : (
                            <>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 800,
                                    color,
                                    lineHeight: "14px",
                                    ...labelSx,
                                }}
                            >
                                {title}
                            </Typography>
                            {description && (
                                <Typography
                                    sx={{
                                        fontSize: "8px",
                                        color: "#999999",
                                        letterSpacing: "0.05em",
                                        ...descriptionSx,
                                    }}
                                >
                                    {description}
                                </Typography>
                            )}
                        </Box>
                        {price && (
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    color: "#f97316",
                                    fontWeight: 600,
                                    ...priceSx,
                                }}
                            >
                                {price}
                            </Typography>
                        )}
                            </>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default BoxRadioGroupNode;
